import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #042E4B 0%, #0A4A6F 100%);
  padding: 4rem;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  margin-bottom: 4rem;
  text-align: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  width: 100%;
  align-items: center;
`;

const LeftSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const PricingCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 4rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 10px;
    background: #EF443C;
  }
`;

const PriceLabel = styled.div`
  font-size: 1.5rem;
  color: #607C90;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Price = styled.div`
  font-size: 5rem;
  font-weight: 900;
  color: #EF443C;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const PriceDetails = styled.div`
  font-size: 1.3rem;
  color: #042E4B;
  font-weight: 500;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 12px;
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  width: 50px;
  height: 50px;
  background: #EF443C;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const StatusBadge = styled(motion.div)`
  background: rgba(34, 197, 94, 0.2);
  border: 2px solid #22C55E;
  color: white;
  padding: 2rem;
  border-radius: 16px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const GoalCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2.5rem;
  border: 2px solid rgba(239, 68, 60, 0.5);
`;

const GoalTitle = styled.div`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #EF443C, #FF6B6B);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 1rem;
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
`;

const GoalText = styled.div`
  margin-top: 1rem;
  font-size: 1.3rem;
  color: white;
  font-weight: 600;
  text-align: center;
`;

const ExtraNote = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  text-align: center;
  font-style: italic;
`;

const Slide10Modelo: React.FC = () => {
  return (
    <Container>
      <Title
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        Modelo de Negócio
      </Title>

      <ContentGrid>
        <LeftSide>
          <PricingCard
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <PriceLabel>Assinatura Anual</PriceLabel>
            <Price>12x R$ 399</Price>
            <PriceDetails>ou R$ 4.788 à vista</PriceDetails>
          </PricingCard>

          <FeatureRow
            as={motion.div}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.25 }}
          >
            <FeatureIcon>👥</FeatureIcon>
            <span>5 usuários inclusos</span>
          </FeatureRow>

          <FeatureRow
            as={motion.div}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.35 }}
          >
            <FeatureIcon>➕</FeatureIcon>
            <span>Usuários extras disponíveis</span>
          </FeatureRow>
        </LeftSide>

        <RightSide>
          <StatusBadge
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <span style={{ fontSize: '2rem' }}>✅</span>
            <span>MVP no ar e funcionando</span>
          </StatusBadge>

          <GoalCard
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <GoalTitle>Meta até Dezembro/2026</GoalTitle>
            <ProgressBar>
              <ProgressFill
                initial={{ width: '0%' }}
                animate={{ width: '35%' }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                35%
              </ProgressFill>
            </ProgressBar>
            <GoalText>100 clientes ativos</GoalText>
          </GoalCard>

          <ExtraNote
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            Modelo SaaS com receita recorrente
          </ExtraNote>
        </RightSide>
      </ContentGrid>
    </Container>
  );
};

export default Slide10Modelo;
